/**
 * BulkDelegationAction — botón «Delegación» de la barra de selección, con su
 * diálogo y el resumen del resultado.
 *
 * Se usa como contenido de `bulkActions` de `ListPage`:
 *
 *   bulkActions={({ selectedRows }) => (
 *     <BulkDelegationAction
 *       companyId={companyId}
 *       entity="virt_vms"              // clave del backend
 *       selectedRows={selectedRows}
 *       onApplied={() => { recargar(); setBulkResetKey((k) => k + 1); }}
 *     />
 *   )}
 *
 * Solo tiene sentido para VMs PROPIAS: el listado ya deshabilita la casilla de
 * las delegadas a mí, y el backend vuelve a comprobarlo. El destino puede ser
 * cualquier empresa activa (el desplegable usa `/companies/all`, igual que la
 * ficha de la VM) o «Quitar delegación».
 *
 * OJO (mismo motivo que en `BulkPlacementAction`): el resumen se pinta antes
 * de avisar al listado. Al aplicar se vacía la selección, `bulkActions` deja
 * de pintarse y este componente se desmontaría con el resumen dentro.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import Dialog from './Dialog';

const NO_CHANGE = '';
const NONE = '__none__';

export default function BulkDelegationAction({
  companyId,
  entity = 'virt_vms',
  selectedRows = [],
  label,
  onApplied,
}) {
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };

  const [rows, setRows] = useState(null);       // selección congelada al abrir
  const [companies, setCompanies] = useState([]);
  const [choice, setChoice] = useState(NO_CHANGE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);   // resumen tras aplicar

  /* El desplegable de la ficha usa la misma lista: todas las empresas activas,
     también aquellas en las que el usuario no está autorizado (delegar es
     justo dar acceso a otra empresa). */
  useEffect(() => {
    let alive = true;
    api.getAuthorizedCompanies()
      .then((list) => { if (alive) setCompanies(Array.isArray(list) ? list : []); })
      .catch(() => { if (alive) setCompanies([]); });
    return () => { alive = false; };
  }, []);

  const open = () => {
    setRows(selectedRows);
    setError('');
    setResult(null);
    // Si todas las seleccionadas comparten delegación, se propone esa misma;
    // si están mezcladas, «Sin cambios» para no pisar nada sin querer.
    const values = new Set(
      selectedRows.map((row) => (
        row.delegated_company_id == null ? NONE : String(row.delegated_company_id)
      )),
    );
    setChoice(values.size === 1 ? [...values][0] : NO_CHANGE);
  };

  const closeResult = () => {
    const payload = result;
    setResult(null);
    onApplied?.(payload);
  };

  const options = useMemo(
    () => companies.filter((company) => String(company.id) !== String(companyId)),
    [companies, companyId],
  );

  const names = useMemo(
    () => (rows || []).slice(0, 5).map((row) => row.name || `#${row.id}`),
    [rows],
  );
  const more = (rows?.length || 0) - names.length;

  const submit = async () => {
    if (choice === NO_CHANGE) {
      setError(tr('bulk_deleg.nothing', 'Elige una empresa o «Quitar delegación».'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = await api.bulkAssignDelegation(
        entity,
        rows.map((row) => row.id),
        choice === NONE ? null : Number(choice),
        companyId,
      );
      // Se anotan los nombres antes de soltar la selección: el resumen de
      // fallos solo trae ids.
      const byId = new Map(rows.map((row) => [row.id, row.name]));
      setRows(null);
      setResult({
        updated: payload?.updated || 0,
        unchanged: payload?.unchanged || 0,
        failed: (payload?.failed || []).map((item) => ({
          ...item,
          name: byId.get(item.id) || `#${item.id}`,
        })),
      });
    } catch (e) {
      setError(e.message || tr('bulk_deleg.error', 'No se pudo cambiar la delegación'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="list-btn-primary"
        onClick={open}
        disabled={!selectedRows.length}
        title={tr('bulk_deleg.open_hint', 'Cambiar la empresa delegada de las máquinas seleccionadas')}
      >
        <span aria-hidden="true">🏢</span>{' '}
        {label || tr('bulk_deleg.open', 'Delegación')}
      </button>

      {rows && (
        <Dialog
          title={tr('bulk_deleg.title', 'Cambiar la delegación')}
          icon="🏢"
          onClose={() => setRows(null)}
          busy={saving}
          maxWidth={520}
          footer={(
            <>
              <button
                type="button"
                className="list-btn-secondary"
                onClick={() => setRows(null)}
                disabled={saving}
              >
                {tr('action.cancel', 'Cancelar')}
              </button>
              <button
                type="button"
                className="list-btn-primary"
                onClick={submit}
                disabled={saving || choice === NO_CHANGE}
              >
                {saving
                  ? tr('bulk_deleg.applying', 'Aplicando…')
                  : tr('bulk_deleg.apply', 'Aplicar a las seleccionadas')}
              </button>
            </>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>
            <strong>{rows.length}</strong>{' '}
            {rows.length === 1
              ? tr('bulk_deleg.row_selected', 'máquina seleccionada')
              : tr('bulk_deleg.rows_selected', 'máquinas seleccionadas')}
            {names.length > 0 && (
              <>
                {' · '}{names.join(', ')}
                {more > 0 ? ` ${tr('bulk.and_more', 'y')} ${more} ${tr('bulk.more', 'más')}` : ''}
              </>
            )}
          </p>

          <label className="bulk-field">
            <span>{tr('bulk_deleg.field', 'Empresa delegada')}</span>
            <select
              className="list-select"
              value={choice}
              onChange={(event) => { setChoice(event.target.value); setError(''); }}
              disabled={saving}
            >
              <option value={NO_CHANGE}>{tr('bulk.no_change', 'Sin cambios')}</option>
              <option value={NONE}>{tr('bulk_deleg.none', '— Quitar delegación —')}</option>
              {options.map((company) => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
            <small className="list-hint">
              {tr(
                'bulk_deleg.hint',
                'La empresa delegada verá la máquina en su inventario, pero no podrá editarla.',
              )}
            </small>
          </label>

          <p className="list-hint">
            {tr(
              'bulk_deleg.owner_only',
              'Solo se cambian las máquinas de las que esta empresa es propietaria.',
            )}
          </p>

          {error && (
            <div className="alert alert-danger" role="alert" style={{ marginTop: 12 }}>
              {error}
            </div>
          )}
        </Dialog>
      )}

      {result && (
        <Dialog
          title={tr('bulk_deleg.done_title', 'Delegación actualizada')}
          icon={result.failed?.length ? '⚠️' : '✅'}
          onClose={closeResult}
          maxWidth={520}
          footer={(
            <button type="button" className="list-btn-primary" onClick={closeResult}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          <p>
            <strong>{result.updated}</strong>{' '}
            {result.updated === 1
              ? tr('bulk_deleg.done_row', 'máquina actualizada')
              : tr('bulk_deleg.done_rows', 'máquinas actualizadas')}
            {result.unchanged > 0 && (
              <> · <strong>{result.unchanged}</strong>{' '}
                {tr('bulk_deleg.unchanged', 'ya tenían esa delegación')}</>
            )}
            {result.failed?.length > 0 && (
              <> · <strong style={{ color: 'var(--red-600)' }}>{result.failed.length}</strong>{' '}
                {result.failed.length === 1
                  ? tr('bulk_deleg.done_failed_one', 'no se pudo cambiar')
                  : tr('bulk_deleg.done_failed', 'no se pudieron cambiar')}</>
            )}
          </p>
          {result.failed?.length > 0 && (
            <ul className="list-hint" style={{ marginTop: 8, paddingLeft: 18 }}>
              {result.failed.slice(0, 10).map((item) => (
                <li key={item.id}>{item.name}: {item.error}</li>
              ))}
              {result.failed.length > 10 && <li>…</li>}
            </ul>
          )}
        </Dialog>
      )}
    </>
  );
}
